import {postVerificationEmail} from 'app/services/apiBackend';
import {User} from 'firebase/auth';
import {captureException} from '@sentry/browser';
import {isAxiosError} from 'axios';

const RATE_LIMIT_MS = 60000;
const MAX_RETRY_ATTEMPTS = 2;

let pendingRetryTimeout: ReturnType<typeof setTimeout> | null = null;
let lastVerificationSent: number | null = null;
let retryAttempts = 0;

export const isRateLimitError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.data?.status === 429;

export const cancelScheduledVerificationEmail = (): void => {
  if (pendingRetryTimeout !== null) {
    clearTimeout(pendingRetryTimeout);
    pendingRetryTimeout = null;
  }
};

export const resetVerificationEmailState = (): void => {
  cancelScheduledVerificationEmail();
  lastVerificationSent = null;
  retryAttempts = 0;
};

const scheduleRetry = (user: User, redirectUrl: string, delayMs: number): void => {
  cancelScheduledVerificationEmail();
  pendingRetryTimeout = setTimeout(() => {
    pendingRetryTimeout = null;
    // scheduleRetry is only ever invoked from retry-enabled code paths.
    attemptSendVerificationEmail(user, redirectUrl, true);
  }, delayMs);
  // Don't let a pending silent retry keep a non-browser process (e.g. tests, SSR) alive.
  (pendingRetryTimeout as any)?.unref?.();
};

const attemptSendVerificationEmail = async (user: User, redirectUrl: string, retry: boolean) => {
  try {
    const token = await user.getIdToken();
    const response = await postVerificationEmail(token, redirectUrl);
    lastVerificationSent = Date.now();
    retryAttempts = 0;
    return response;
  } catch (error) {
    if (isRateLimitError(error)) {
      lastVerificationSent = Date.now();
      if (!retry) {
        throw error;
      }
      retryAttempts += 1;
      if (retryAttempts >= MAX_RETRY_ATTEMPTS) {
        captureException(
          new Error(`Sending Verification Mail failed after ${retryAttempts} Retries with 429 status-code`)
        );
        retryAttempts = 0;
        return;
      }
      scheduleRetry(user, redirectUrl, RATE_LIMIT_MS);
      return;
    }
    if (isAxiosError(error)) {
      const responseError = error.response?.data ?? error.message;
      captureException(new Error('Could not send email verification'), {extra: {responseError}});
    } else {
      captureException(error);
    }
    return;
  }
};

// When retry is false, a 429 rate-limit response is rejected to the caller instead of being
// silently retried in the background, so the caller can drive its own retry timing.
export const sendVerificationEmail = async (user: User, redirectUrl: string, retry: boolean = true) => {
  if (retry && lastVerificationSent !== null) {
    const elapsedSinceLastSent = Date.now() - lastVerificationSent;
    if (elapsedSinceLastSent < RATE_LIMIT_MS) {
      scheduleRetry(user, redirectUrl, RATE_LIMIT_MS - elapsedSinceLastSent);
      return;
    }
  }
  return attemptSendVerificationEmail(user, redirectUrl, retry);
};
